/**
 * Cart Progress Bar JavaScript
 * Handles dynamic updates and animations for the cart progress bar
 */

/**
 * Collapse duplicate pound signs from theme copy (e.g. "Add ££[remaining]") after [remaining] substitution.
 * @param {string} str
 * @returns {string}
 */
function collapseDuplicateCurrencySymbols(str) {
  if (str == null) return '';
  return String(str).replace(/£{2,}/g, '£');
}

/** Unified cart change dispatcher — quick-add / product-form call this; progress bar listens via on:cart:change */
if (!window.ccDispatchCartChange) {
  window.ccDispatchCartChange = function ccDispatchCartChange(detail) {
    document.dispatchEvent(new CustomEvent('on:cart:change', {
      bubbles: true,
      detail: Object.assign({ cartUpdated: true }, detail || {})
    }));
  };
}

class CartProgressBar {
  constructor() {
    // Try to find progress bar (could be in cart page or cart drawer)
    this.progressBar = document.getElementById('cart-progress-bar');
    this.milestones = this.getMilestones(); // Get dynamic milestones
    
    // Freebies feature toggle — independent of the progress bar visual toggle
    const freebiesEnabled = theme.settings.cartProgressBar?.freebiesEnabled === true;

    // Get free product variant IDs only when freebies are enabled
    const freebie2Enabled = freebiesEnabled && theme.settings.cartProgressBar?.freebie2Enabled !== false;
    const variantIdString = freebie2Enabled ? theme.settings.cartProgressBar?.freeProductVariantId : null;
    this.freeProductVariantId = variantIdString ? parseInt(variantIdString, 10) : null;

    const freebie1Enabled = freebiesEnabled && theme.settings.cartProgressBar?.freebie1Enabled !== false;
    const freebie3IdString = freebie1Enabled ? theme.settings.cartProgressBar?.freebie3VariantId : null;
    this.freebie3VariantId = freebie3IdString ? parseInt(freebie3IdString, 10) : null;

    // Read surcharge variant ID from the DOM (set by cart-postcode-date-row.liquid)
    const surchargeEl = document.querySelector('[data-surcharge-variant-id]');
    const surchargeIdString = surchargeEl ? surchargeEl.dataset.surchargeVariantId : null;
    this.surchargeVariantId = surchargeIdString ? parseInt(surchargeIdString, 10) : null;

    // Flag to prevent multiple simultaneous operations
    this.isProcessingFreeProduct = false;
    this.isShowingMilestonePopup = false; // Prevent concurrent popup calls
    
    // Track which milestones have already shown their popup
    this.shownPopups = new Set();
    
    // Track current milestone to detect changes
    this.currentMilestone = 0;
    
    // Track last celebrated milestone to prevent duplicate celebrations
    this.lastCelebratedMilestone = 0;
    
    // console.log('Cart Progress Bar: Initialized lastCelebratedMilestone to:', this.lastCelebratedMilestone);
    
    // Track if cart was actually modified (add/remove products)
    this.cartWasModified = false;
    
    // console.log('Cart Progress Bar: Constructor - Using variant ID from global settings:', this.freeProductVariantId);
    // console.log('Cart Progress Bar: Constructor - Progress bar element:', this.progressBar);
    // console.log('Cart Progress Bar: Constructor - Theme settings:', theme.settings.cartProgressBar);
    
    // Check if progress bar element exists
    if (!this.progressBar) {
      // console.log('Cart Progress Bar: ❌ Progress bar element not found!');
      // console.log('Cart Progress Bar: Available elements with cart-progress-bar class:', document.querySelectorAll('.cart-progress-bar'));
      // console.log('Cart Progress Bar: Available elements with cart-progress-bar id:', document.querySelectorAll('#cart-progress-bar'));
    } else {
      // console.log('Cart Progress Bar: ✅ Progress bar element found');
    }
    
    this.init();
  }

  getMilestones() {
    // Get milestone amounts from global theme settings (in dollars) and convert to cents
    const milestone1 = (theme.settings.cartProgressBar?.milestone1 || 5) * 100;
    const milestone2 = (theme.settings.cartProgressBar?.milestone2 || 10) * 100;
    const milestone3 = (theme.settings.cartProgressBar?.milestone3 || 15) * 100;
    
    return [0, milestone1, milestone2, milestone3];
  }

  /**
   * Mark cart as user-modified so milestone-3 popup eligibility is re-evaluated.
   * Skips internal milestone-removal events that should not re-trigger the popup.
   * @param {object} [detail] - Event detail from on:cart:change
   */
  markCartModifiedForPopup(detail) {
    const source = detail?.source || '';
    if (source === 'milestone3_removal') return;

    this.cartWasModified = true;
    sessionStorage.removeItem('milestone3-popup-shown');
  }

  init() {
    // Always bind events to handle cart updates even if progress bar element doesn't exist
    this.bindEvents();
    
    // console.log('Cart Progress Bar: Initializing with settings:', theme.settings.cartProgressBar);
    // console.log('Cart Progress Bar: Free product variant ID:', this.freeProductVariantId);
    // console.log('Cart Progress Bar: Milestones:', this.milestones);
    
    if (!this.progressBar) {
      // console.log('Cart Progress Bar: No progress bar element found (not on cart page, but continuing to monitor milestones)');
    }
    
    // Always check for milestone celebration, even without progress bar element
    this.updateProgress();
  }

  // Debounced updateProgress — collapses rapid-fire events (on:cart:change + on:cart:add +
  // dispatch:cart-drawer:refresh all arrive within ~100ms of each other after every ATC).
  _scheduleUpdateProgress() {
    clearTimeout(this._updateProgressTimer);
    this._updateProgressTimer = setTimeout(() => this.updateProgress(), 150);
  }

  bindEvents() {
    // Listen for cart updates (including ccDispatchCartChange from quick-add / drawer refresh)
    document.addEventListener('on:cart:change', (event) => {
      const detail = event.detail || {};
      if (detail.cartUpdated || detail.source || detail.cart) {
        this.markCartModifiedForPopup(detail);
      }
      // Use cart data already in the event for an instant visual update, then
      // let the debounced call do the full milestone check once events settle.
      if (detail.cart && typeof detail.cart.total_price === 'number') {
        this.updateProgressFromTotal(detail.cart.total_price);
      }
      this._scheduleUpdateProgress();
    });

    // Listen for cart add events (from product form)
    document.addEventListener('on:cart:add', () => {
      this.markCartModifiedForPopup();
      this._scheduleUpdateProgress();
    });

    // Listen for cart drawer updates
    document.addEventListener('dispatch:cart-drawer:refresh', () => {
      this._scheduleUpdateProgress();
    });

    // Listen for line item changes
    document.addEventListener('on:line-item:change', () => {
      this.markCartModifiedForPopup();
      this._scheduleUpdateProgress();
    });

    // Listen for cart errors (might need to refresh progress)
    document.addEventListener('on:cart:error', () => {
      this._scheduleUpdateProgress();
    });

    // Listen for direct free product check requests
    document.addEventListener('on:free-product:check', () => {
      this.markCartModifiedForPopup();
      this._scheduleUpdateProgress();
    });

    // Listen for page load
    document.addEventListener('DOMContentLoaded', () => {
      // console.log('Cart Progress Bar: DOM loaded, updating progress');
      this.updateProgress();
    });

    // Listen for popup close events (use capture to avoid conflicts)
    document.addEventListener('on:milestone-popup:closed', async (event) => {
      // Reset the showing flag to allow popup to show again if needed
      this.isShowingMilestonePopup = false;
      
      const productSelected = event.detail?.productSelected;
      
      // If user closed popup without selecting, the flag is already set in milestone-popup.liquid
      // We don't show popup again - user can use replace button instead
      if (!productSelected) {
        // Update replace button visibility immediately
        const cartTotalExcludingFreeProduct = await this.getCartTotalExcludingFreeProduct();
        if (cartTotalExcludingFreeProduct !== null) {
          const progressData = this.calculateProgress(cartTotalExcludingFreeProduct);
          await this.updateReplaceButtonVisibility(progressData.currentMilestone);
        }
      }
      
      event.stopPropagation(); // Prevent further event propagation
    }, true); // Use capture phase
    
    // Listen for milestone 3 popup products changes (added/removed)
    document.addEventListener('on:milestone3-popup-products:changed', async (event) => {
      // Immediately update replace button visibility
      const cartTotalExcludingFreeProduct = await this.getCartTotalExcludingFreeProduct();
      if (cartTotalExcludingFreeProduct !== null) {
        const progressData = this.calculateProgress(cartTotalExcludingFreeProduct);
        await this.updateReplaceButtonVisibility(progressData.currentMilestone);
      }
    });
  }

  async updateProgress() {
    // console.log('Cart Progress Bar: Updating progress...');

    // Always re-check for progress bar element in case it was replaced via AJAX (e.g., cart drawer refresh)
    const progressBarElement = document.getElementById('cart-progress-bar');
    if (progressBarElement) {
      // Update reference if element was replaced or wasn't found before
      if (!this.progressBar || this.progressBar !== progressBarElement) {
        this.progressBar = progressBarElement;
        // console.log('Cart Progress Bar: Updated progress bar element reference');
      }
    } else if (this.progressBar && !document.contains(this.progressBar)) {
      // Element was removed from DOM
      this.progressBar = null;
      // console.log('Cart Progress Bar: Progress bar element removed from DOM');
    }

    // Refresh milestones in case settings changed
    this.milestones = this.getMilestones();
    // console.log('Cart Progress Bar: Milestones:', this.milestones);

    // Get cart total from the page EXCLUDING free product for milestone calculation
    const cartTotalExcludingFreeProduct = await this.getCartTotalExcludingFreeProduct();
    if (cartTotalExcludingFreeProduct === null) {
      // console.log('Cart Progress Bar: No cart total found, skipping update');
      return;
    }

    // console.log('Cart Progress Bar: Cart total (excluding free product):', cartTotalExcludingFreeProduct);
    const progressData = this.calculateProgress(cartTotalExcludingFreeProduct);
    // console.log('Cart Progress Bar: Progress data:', progressData);
    
    // Store whether to show popup before resetting the flag
    const shouldShowPopup = progressData.currentMilestone === 3 && this.cartWasModified;
    const lastMilestoneWas3 = this.lastCelebratedMilestone === 3;
    
    // Capture cartWasModified before it might be reset
    const wasCartModified = this.cartWasModified;
    
    // Check if milestone is moving forward or backward
    const isMovingForward = progressData.currentMilestone > this.currentMilestone;
    const isMovingBackward = progressData.currentMilestone < this.currentMilestone;
    
    // Check if milestone changed and reset popup state if it did AND cart was modified
    if (this.currentMilestone !== progressData.currentMilestone && this.cartWasModified) {
      // console.log('Cart Progress Bar: Milestone changed from', this.currentMilestone, 'to', progressData.currentMilestone, 'and cart was modified');
      this.resetPopupState();
      
      // Reset lastCelebratedMilestone if milestone decreased (going backwards)
      if (progressData.currentMilestone < this.currentMilestone) {
        this.lastCelebratedMilestone = -1; // Set to -1 to allow re-celebrating when going forward again
      }
      
      this.currentMilestone = progressData.currentMilestone;
      this.cartWasModified = false; // Reset modification flag
    } else if (this.currentMilestone !== progressData.currentMilestone) {
      // console.log('Cart Progress Bar: Milestone changed from', this.currentMilestone, 'to', progressData.currentMilestone, 'but cart was not modified');
      
      // Also reset if milestone decreased (even without cart modification)
      if (progressData.currentMilestone < this.currentMilestone) {
        this.lastCelebratedMilestone = -1;
      }
      
      this.currentMilestone = progressData.currentMilestone;
    }
    
    // Only render progress bar UI if the element exists
    if (this.progressBar) {
      await this.renderProgress(progressData);
    } else {
      // Even without progress bar, update the replace button visibility
      await this.updateReplaceButtonVisibility(progressData.currentMilestone);
    }
    
    // Always check for milestone celebration, with or without progress bar
    // Check if popup should show based on session storage flags
    const popupShouldShow = this.shouldShowMilestone3Popup(
      progressData.currentMilestone,
      shouldShowPopup,
      wasCartModified
    );
    
    let milestoneCelebrated = false;
    
    if (progressData.currentMilestone === 3) {
      if (popupShouldShow) {
        const popupShown = await this.celebrateMilestone(3, wasCartModified, isMovingForward);
        this.lastCelebratedMilestone = popupShown ? 3 : 0;
        milestoneCelebrated = true;
      } else if (this.lastCelebratedMilestone !== 3 && isMovingForward) {
        // First time crossing milestone 3 (e.g. quick-add path with no on:cart:add)
        const popupShown = await this.celebrateMilestone(3, wasCartModified, isMovingForward);
        this.lastCelebratedMilestone = popupShown ? 3 : 0;
        milestoneCelebrated = true;
      }
    } else if (progressData.currentMilestone > 0 && progressData.currentMilestone !== this.lastCelebratedMilestone && isMovingForward) {
      // Celebrating other milestones (not milestone 3) - only when moving forward
      await this.celebrateMilestone(progressData.currentMilestone, wasCartModified, isMovingForward);
      this.lastCelebratedMilestone = progressData.currentMilestone;
      milestoneCelebrated = true;
    }
    
    // Free product handling is now done in cart-items.js, no need to handle here
    // this.handleFreeProduct(cartTotalExcludingFreeProduct, progressData.currentMilestone);
  }

  getCartTotal() {
    // Try to get cart total from various sources
    let cartTotal = null;

    // Method 1: From cart summary (most reliable for cart page)
    const cartSummary = document.querySelector('.cart__summary p.font-bold');
    if (cartSummary) {
      const totalText = cartSummary.textContent.trim();
      cartTotal = this.parseMoney(totalText);
      // console.log('Cart Progress Bar: Found total from cart summary:', cartTotal);
    }

    // Method 2: From cart total element
    if (!cartTotal) {
      const totalElement = document.querySelector('[data-cart-total]');
      if (totalElement) {
        cartTotal = parseInt(totalElement.dataset.cartTotal);
        // console.log('Cart Progress Bar: Found total from data attribute:', cartTotal);
      }
    }

    // Method 3: From cart drawer total
    if (!cartTotal) {
      const cartDrawerTotal = document.querySelector('.cart-drawer__summary .cart-drawer__total');
      if (cartDrawerTotal) {
        const totalText = cartDrawerTotal.textContent.trim();
        cartTotal = this.parseMoney(totalText);
        // console.log('Cart Progress Bar: Found total from cart drawer:', cartTotal);
      }
    }

    // Method 4: From global cart object
    if (!cartTotal && window.cart) {
      cartTotal = window.cart.total_price;
      // console.log('Cart Progress Bar: Found total from window.cart:', cartTotal);
    }

    // Method 5: From Shopify global
    if (!cartTotal && window.Shopify && window.Shopify.theme && window.Shopify.theme.cart) {
      cartTotal = window.Shopify.theme.cart.total_price;
      // console.log('Cart Progress Bar: Found total from Shopify.theme.cart:', cartTotal);
    }

    // Method 6: Fetch from cart API as fallback
    if (!cartTotal) {
      // console.log('Cart Progress Bar: No total found, fetching from API...');
      fetch(`${theme.routes.cart}.js`)
        .then(response => response.json())
        .then(cartData => {
          if (cartData.total_price) {
            // console.log('Cart Progress Bar: Found total from API:', cartData.total_price);
            this.updateProgressFromTotal(cartData.total_price);
          }
        })
        .catch(error => {
          // console.log('Error fetching cart total:', error);
        });
    }

    return cartTotal;
  }

  async getCartTotalExcludingFreeProduct() {
    // console.log('Cart Progress Bar: ===== CALCULATING CART TOTAL EXCLUDING FREE PRODUCT =====');
    
    try {
      const cartData =
        typeof window.ccGetCartSnapshot === 'function'
          ? await window.ccGetCartSnapshot()
          : await fetch('/cart.js').then((r) => r.json());
      
      // console.log('Cart Progress Bar: Fresh cart data from API:', cartData);
      
      if (!cartData || !cartData.items) {
        // console.log('Cart Progress Bar: No cart data or items found');
        return this.getCartTotal(); // Fallback to original method
      }
      
      // Log all cart items with their properties for debugging
      // console.log('Cart Progress Bar: ===== CART ITEMS AND PROPERTIES =====');
      cartData.items.forEach((item, index) => {
        // console.log(`Cart Progress Bar: Item ${index + 1}:`, {
        //   title: item.title,
        //   variant_id: item.variant_id,
        //   final_line_price: item.final_line_price,
        //   properties: item.properties || 'No properties'
        // });
      });
      
      // Use the same calculation as cart-items.js
      // Exclude freebie 1, freebie 3, and milestone popup products
      const totalExcludingFreeProduct = cartData.items
        .filter(item => {
          const isFreeProduct = item.variant_id === this.freeProductVariantId;
          const isFreebie3 = this.freebie3VariantId && item.variant_id === this.freebie3VariantId;
          const isMilestonePopupProduct = item.properties && item.properties._3rd_milestone_popup === 'true';
          const isSurcharge = this.surchargeVariantId && item.variant_id === this.surchargeVariantId;
          return !isFreeProduct && !isFreebie3 && !isMilestonePopupProduct && !isSurcharge;
        })
        .reduce((total, item) => total + (item.final_line_price), 0);
      
      // console.log('💰 ===== FINAL CALCULATION (API METHOD) =====');
      // console.log('💰 Original cart total:', cartData.total_price, 'cents = $', (cartData.total_price / 100));
      // console.log('💰 Free product variant ID to exclude:', this.freeProductVariantId);
      // console.log('💰 Cart total excluding free product:', totalExcludingFreeProduct, 'cents = $', (totalExcludingFreeProduct / 100));
      
      // Log all items for debugging
      cartData.items.forEach((item, index) => {
        const isFreeProduct = item.variant_id === this.freeProductVariantId;
        // console.log(`💰 Item ${index + 1}: Variant ID = ${item.variant_id}, Price = ${item.final_line_price} cents = $${(item.final_line_price / 100)}, ${isFreeProduct ? '🎁 FREE PRODUCT' : 'Regular product'}`);
      });
      
      if (totalExcludingFreeProduct !== cartData.total_price) {
        // console.log('✅ Free product successfully excluded from milestone calculation');
        // console.log('✅ This means the milestone calculation will use:', totalExcludingFreeProduct, 'cents = $', (totalExcludingFreeProduct / 100));
      } else {
        // console.log('⚠️ No free product found to exclude');
        // console.log('⚠️ This means the milestone calculation will use the full cart total:', cartData.total_price, 'cents = $', (cartData.total_price / 100));
      }
      
      return totalExcludingFreeProduct;
      
    } catch (error) {
      // console.log('Cart Progress Bar: Error fetching cart data, falling back to DOM method:', error);
      return this.getCartTotal(); // Fallback to original method
    }
  }


  async updateProgressFromTotal(cartTotal) {
    // Use cart total excluding free product for milestone calculation
    const cartTotalExcludingFreeProduct = await this.getCartTotalExcludingFreeProduct();
    const progressData = this.calculateProgress(cartTotalExcludingFreeProduct);
    this.renderProgress(progressData);
  }

  parseMoney(moneyString) {
    // Remove currency symbols and convert to cents
    const cleanString = moneyString.replace(/[^\d.,]/g, '');
    const number = parseFloat(cleanString.replace(',', '.'));
    return Math.round(number * 100); // Convert to cents
  }

  calculateProgress(cartTotal) {
    let currentMilestone = 0;
    let progressPercentage = 0;
    let nextMilestone = this.milestones[1];
    let remainingAmount = 0;
    
    // Milestone positions with equal spacing (not based on dollar values)
    // 0% (start), 33.33% (milestone 1), 66.66% (milestone 2), 100% (milestone 3)
    const milestone1Percentage = 33.33;
    const milestone2Percentage = 66.66;

    if (cartTotal >= this.milestones[3]) {
      currentMilestone = 3;
      progressPercentage = 100;
      nextMilestone = this.milestones[3];
    } else if (cartTotal >= this.milestones[2]) {
      currentMilestone = 2;
      // Calculate progress between milestone 2 (66.66%) and 3 (100%)
      progressPercentage = milestone2Percentage + ((cartTotal - this.milestones[2]) / (this.milestones[3] - this.milestones[2])) * (100 - milestone2Percentage);
      nextMilestone = this.milestones[3];
    } else if (cartTotal >= this.milestones[1]) {
      currentMilestone = 1;
      // Calculate progress between milestone 1 (33.33%) and 2 (66.66%)
      progressPercentage = milestone1Percentage + ((cartTotal - this.milestones[1]) / (this.milestones[2] - this.milestones[1])) * (milestone2Percentage - milestone1Percentage);
      nextMilestone = this.milestones[2];
    } else {
      currentMilestone = 0;
      // Calculate progress from 0 to milestone 1 (33.33%)
      progressPercentage = (cartTotal / this.milestones[1]) * milestone1Percentage;
      nextMilestone = this.milestones[1];
    }

    remainingAmount = nextMilestone - cartTotal;

    return {
      currentMilestone,
      progressPercentage: Math.min(progressPercentage, 100),
      nextMilestone,
      remainingAmount,
      cartTotal
    };
  }

  getSegmentFills(progressPercentage) {
    const p = Math.min(progressPercentage, 100);
    let seg1 = 0;
    let seg2 = 0;
    let seg3 = 0;
    if (p >= 33.33) {
      seg1 = 100;
    } else {
      seg1 = (p / 33.33) * 100;
    }
    if (p >= 66.66) {
      seg2 = 100;
    } else if (p >= 33.33) {
      seg2 = ((p - 33.33) / 33.33) * 100;
    }
    if (p >= 100) {
      seg3 = 100;
    } else if (p >= 66.66) {
      seg3 = ((p - 66.66) / 33.34) * 100;
    }
    return [Math.round(seg1), Math.round(seg2), Math.round(seg3)];
  }

  async renderProgress(data) {
    if (!this.progressBar) {
      return;
    }

    const trackElement = this.progressBar.querySelector('.cart-progress-bar__track');
    const titleElement = this.progressBar.querySelector('.cart-progress-bar__title');
    const subtitleElement = this.progressBar.querySelector('.cart-progress-bar__subtitle');
    const stepElements = this.progressBar.querySelectorAll('.cart-progress-bar__step');
    const benefitElements = this.progressBar.querySelectorAll('.cart-progress-bar__benefit-item');

    // Update track data-progress
    if (trackElement) {
      trackElement.dataset.progress = Math.round(data.progressPercentage);
    }

    // Update line segment fills
    const [seg1Fill, seg2Fill, seg3Fill] = this.getSegmentFills(data.progressPercentage);
    const lineElements = this.progressBar.querySelectorAll('.cart-progress-bar__line');
    const fillValues = [seg1Fill, seg2Fill, seg3Fill];
    lineElements.forEach((line, index) => {
      const fillEl = line.querySelector('.cart-progress-bar__line-fill');
      if (fillEl && fillValues[index] !== undefined) {
        fillEl.style.width = `${fillValues[index]}%`;
        fillEl.dataset.fill = fillValues[index];
      }
    });

    // Update step completion status
    stepElements.forEach((step, index) => {
      const milestoneIndex = index + 1;
      if (data.currentMilestone >= milestoneIndex) {
        step.classList.add('cart-progress-bar__step--completed');
      } else {
        step.classList.remove('cart-progress-bar__step--completed');
      }
    });

    // Update title with milestone-specific messages
    if (titleElement) {
      const titleMessage = collapseDuplicateCurrencySymbols(
        this.getMilestoneTitleMessage(data.currentMilestone, data.cartTotal, data.remainingAmount, data.nextMilestone)
      );
      titleElement.innerHTML = titleMessage;
    }

    // Update subtitle with next milestone information
    if (subtitleElement) {
      const subtitleMessage = collapseDuplicateCurrencySymbols(
        this.getMilestoneSubtitleMessage(data.currentMilestone, data.remainingAmount, data.nextMilestone)
      );
      subtitleElement.textContent = subtitleMessage;
    }

    // Update benefits
    benefitElements.forEach((benefit, index) => {
      const checkmark = benefit.querySelector('span');
      if (checkmark) {
        if (index < data.currentMilestone) {
          checkmark.className = 'text-success';
          checkmark.textContent = '✓';
        } else {
          checkmark.className = 'text-muted';
          checkmark.textContent = '○';
        }
      }
    });

    // Add completion classes for all milestones up to and including the current one
    for (let m = 1; m <= data.currentMilestone; m++) {
      this.progressBar.classList.add(`milestone-${m}-completed`);
    }
    
    // Update replace button visibility based on milestone 3 status
    this.updateReplaceButtonVisibility(data.currentMilestone);
  }

  /**
   * Update the visibility of the replace freebie button
   * Button should be visible when:
   * 1. Milestone 3 is completed (current_milestone >= 3)
   * 2. AND either:
   *    - There are 3rd milestone popup products in the cart, OR
   *    - User manually removed Freebie 2 (so they can select a new one via button), OR
   *    - User dismissed popup without selecting (so they can select from button instead)
   */
  async updateReplaceButtonVisibility(currentMilestone) {
    // Find all replace button containers (could be in cart drawer and/or cart page)
    const buttonContainers = document.querySelectorAll('.cart-replace-button-container');
    
    if (buttonContainers.length === 0) {
      return; // No button containers found
    }
    
    // Check if milestone 3 is reached
    const milestone3Reached = currentMilestone >= 3;
    
    // Check if there are 3rd milestone popup products in cart
    let hasPopupProducts = false;
    
    try {
      const cartData = typeof window.ccGetCartSnapshot === 'function'
        ? await window.ccGetCartSnapshot()
        : await fetch('/cart.js').then(r => r.json());

      if (cartData && cartData.items) {
        hasPopupProducts = cartData.items.some(item =>
          item.properties && item.properties._3rd_milestone_popup === 'true'
        );
      }
    } catch (error) {
      console.error('Cart Progress Bar: Error checking for popup products:', error);
    }

    // Check if user manually removed Freebie 2
    const manuallyRemoved = sessionStorage.getItem('milestone3-popup-manually-removed') === 'true';
    
    // Check if user dismissed popup without selecting
    const dismissedWithoutSelection = sessionStorage.getItem('milestone3-popup-dismissed-without-selection') === 'true';
    
    // Button should be visible when milestone 3 is reached AND (there are popup products OR user manually removed Freebie 2 OR user dismissed without selection)
    const shouldShowButton = milestone3Reached && (hasPopupProducts || manuallyRemoved || dismissedWithoutSelection);
    
    buttonContainers.forEach(container => {
      if (shouldShowButton) {
        container.style.display = '';
      } else {
        container.style.display = 'none';
      }
    });
  }

  getMilestoneTitleMessage(currentMilestone, cartTotal, remainingAmount, nextMilestone) {
    // Check if we're inside cart-drawer
    const isInCartDrawer = this.progressBar && this.progressBar.closest('cart-drawer') !== null;
    
    // Get milestone amount from global theme settings (in dollars)
    let milestoneAmount;
    switch (currentMilestone) {
      case 1:
        milestoneAmount = theme.settings.cartProgressBar?.milestone1 || 5;
        break;
      case 2:
        milestoneAmount = theme.settings.cartProgressBar?.milestone2 || 10;
        break;
      case 3:
        milestoneAmount = theme.settings.cartProgressBar?.milestone3 || 15;
        break;
      default:
        milestoneAmount = 0;
    }
    
    // For cart-drawer: show title BEFORE reaching milestones, show final message when completed
    if (isInCartDrawer) {
      switch (currentMilestone) {
        case 0:
          // Show subtitle-style message as title in cart-drawer
          const remainingFormatted = this.formatMoney(remainingAmount);
          const nextMilestoneAmount = theme.settings.cartProgressBar?.milestone1;
          const subtitle0 = theme.settings.cartProgressBar?.milestone0Subtitle;
          if (!subtitle0) return '';
          return subtitle0.replace('[remaining]', remainingFormatted).replace('[next]', nextMilestoneAmount);
        case 1:
          const remaining1 = this.formatMoney(remainingAmount);
          const nextAmount1 = theme.settings.cartProgressBar?.milestone2;
          const subtitle1 = theme.settings.cartProgressBar?.milestone1Subtitle;
          if (!subtitle1) return '';
          return subtitle1.replace('[remaining]', remaining1).replace('[next]', nextAmount1);
        case 2:
          const remaining2 = this.formatMoney(remainingAmount);
          const nextAmount2 = theme.settings.cartProgressBar?.milestone3;
          const subtitle2 = theme.settings.cartProgressBar?.milestone2Subtitle;
          if (!subtitle2) return '';
          return subtitle2.replace('[remaining]', remaining2).replace('[next]', nextAmount2);
        case 3:
          // Show final message when all milestones achieved in cart-drawer
          const finalMessage = theme.settings.cartProgressBar?.milestone3Subtitle;
          return finalMessage || '';
        default:
          return '';
      }
    }
    
    // Default behavior for cart page (not cart-drawer)
    switch (currentMilestone) {
      case 0:
        return ''; // No title shown before reaching first milestone
      case 1:
        const title1 = theme.settings.cartProgressBar?.milestone1Title;
        if (!title1) return '';
        return title1.replace('[amount]', milestoneAmount);
      case 2:
        const title2 = theme.settings.cartProgressBar?.milestone2Title;
        if (!title2) return '';
        return title2.replace('[amount]', milestoneAmount);
      case 3:
        const title3 = theme.settings.cartProgressBar?.milestone3Title;
        if (!title3) return '';
        return title3.replace('[amount]', milestoneAmount);
      default:
        return ''; // No title shown for unknown milestones
    }
  }

  getMilestoneSubtitleMessage(currentMilestone, remainingAmount, nextMilestone) {
    const remainingFormatted = this.formatMoney(remainingAmount);
    
    // Get next milestone amount from global theme settings (in dollars)
    let nextMilestoneAmount;
    switch (currentMilestone) {
      case 0:
        nextMilestoneAmount = theme.settings.cartProgressBar?.milestone1;
        break;
      case 1:
        nextMilestoneAmount = theme.settings.cartProgressBar?.milestone2;
        break;
      case 2:
        nextMilestoneAmount = theme.settings.cartProgressBar?.milestone3;
        break;
      default:
        nextMilestoneAmount = 0;
    }

    switch (currentMilestone) {
      case 0:
        const subtitle0 = theme.settings.cartProgressBar?.milestone0Subtitle;
        if (!subtitle0) return '';
        return subtitle0.replace('[remaining]', remainingFormatted).replace('[next]', nextMilestoneAmount);
      case 1:
        const subtitle1 = theme.settings.cartProgressBar?.milestone1Subtitle;
        if (!subtitle1) return '';
        return subtitle1.replace('[remaining]', remainingFormatted).replace('[next]', nextMilestoneAmount);
      case 2:
        const subtitle2 = theme.settings.cartProgressBar?.milestone2Subtitle;
        if (!subtitle2) return '';
        return subtitle2.replace('[remaining]', remainingFormatted).replace('[next]', nextMilestoneAmount);
      case 3:
        const subtitle3 = theme.settings.cartProgressBar?.milestone3Subtitle;
        return subtitle3 || '';
      default:
        return '';
    }
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return dollars.toFixed(2);
  }

  /**
   * Show a milestone achievement notification
   */
  showMilestoneNotification(milestone) {
    // Create notification element if it doesn't exist
    let notification = document.getElementById('milestone-notification');
    if (!notification) {
      notification = document.createElement('div');
      notification.id = 'milestone-notification';
      notification.className = 'milestone-notification';
      document.body.appendChild(notification);
    }
    
    // Get milestone message from theme settings
    let achievementMessage = '';
    switch (milestone) {
      case 1:
        achievementMessage = theme.settings.cartProgressBar?.milestone1Notification || 'Free Shipping!';
        break;
      case 2:
        achievementMessage = theme.settings.cartProgressBar?.milestone2Notification || 'First Reward!';
        break;
      case 3:
        achievementMessage = theme.settings.cartProgressBar?.milestone3Notification || 'All Rewards! 🎊';
        break;
      default:
        achievementMessage = 'Milestone Complete!';
    }
    
    // Set notification content (header removed, only show message)
    notification.innerHTML = `
      <div class="milestone-notification__content">
        <div class="milestone-notification__body">
          <div class="milestone-notification__line">${achievementMessage}</div>
        </div>
      </div>
    `;
    
    // Show notification with animation
    notification.classList.add('show');
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        notification.remove();
      }, 300); // Wait for animation to complete
    }, 3000);
  }

  async showMilestonePopup() {
    if (this.isShowingMilestonePopup) {
      this._milestonePopupPending = true;
      return false;
    }

    this.isShowingMilestonePopup = true;

    try {
      const manuallyRemoved = sessionStorage.getItem('milestone3-popup-manually-removed');
      if (manuallyRemoved === 'true') return false;

      const dismissedWithoutSelection = sessionStorage.getItem('milestone3-popup-dismissed-without-selection');
      if (dismissedWithoutSelection === 'true') return false;

      const hasPopupProducts = await this.checkForExistingPopupProducts();
      if (hasPopupProducts) return false;

      const popupProductSelected = sessionStorage.getItem('milestone3-popup-product-selected');
      if (popupProductSelected === 'true' && !hasPopupProducts) {
        sessionStorage.removeItem('milestone3-popup-product-selected');
        sessionStorage.removeItem('milestone3-popup-shown');
      }

      if (!window.milestonePopup) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      if (!window.milestonePopup) {
        console.warn('Cart Progress Bar: milestonePopup not ready — will retry');
        return false;
      }

      if (window.milestonePopup.overlay?.classList.contains('show')) {
        return true;
      }

      await window.milestonePopup.show('normal');
      sessionStorage.setItem('milestone3-popup-shown', 'true');
      return true;
    } finally {
      this.isShowingMilestonePopup = false;
      if (this._milestonePopupPending) {
        this._milestonePopupPending = false;
        setTimeout(() => this.showMilestonePopup(), 200);
      }
    }
  }

  async checkForExistingPopupProducts() {
    try {
      // console.log('Cart Progress Bar: Checking for existing 3rd milestone popup products...');
      
      const cartData = typeof window.ccGetCartSnapshot === 'function'
        ? await window.ccGetCartSnapshot()
        : await fetch('/cart.js').then(r => r.json());

      if (!cartData || !cartData.items) {
        // console.log('Cart Progress Bar: No cart data found');
        return false;
      }
      
      // Check for items with _3rd_milestone_popup property
      const popupProducts = cartData.items.filter(item => 
        item.properties && item.properties._3rd_milestone_popup === 'true'
      );
      
      // console.log('Cart Progress Bar: Found', popupProducts.length, 'existing popup products in cart');
      
      if (popupProducts.length > 0) {
        // console.log('Cart Progress Bar: Existing popup products:', popupProducts.map(item => item.title));
        return true;
      }
      
      // console.log('Cart Progress Bar: No existing popup products found - popup can be shown');
      return false;
      
    } catch (error) {
      console.error('Cart Progress Bar: Error checking for existing popup products:', error);
      return false; // Default to allowing popup if check fails
    }
  }

  resetPopupState() {
    // Reset popup state when cart is re-rendered or milestone changes
    this.shownPopups.clear();
    
    // console.log('Cart Progress Bar: Reset popup state');
  }

  shouldShowMilestone3Popup(currentMilestone, shouldShowPopup, wasCartModified = false) {
    if (currentMilestone !== 3) {
      return false;
    }

    const manuallyRemoved = sessionStorage.getItem('milestone3-popup-manually-removed');
    if (manuallyRemoved === 'true') {
      return false;
    }

    const dismissedWithoutSelection = sessionStorage.getItem('milestone3-popup-dismissed-without-selection');
    if (dismissedWithoutSelection === 'true') {
      return false;
    }

    const popupShown = sessionStorage.getItem('milestone3-popup-shown');
    const popupProductSelected = sessionStorage.getItem('milestone3-popup-product-selected');

    if (popupProductSelected === 'true') {
      return false;
    }

    // Use captured wasCartModified — this.cartWasModified may already be reset in updateProgress
    if (wasCartModified && !dismissedWithoutSelection) {
      return true;
    }

    if (!popupShown && shouldShowPopup && !dismissedWithoutSelection) {
      return true;
    }

    return false;
  }

  async celebrateMilestone(milestone, cartWasModified = false, isMovingForward = true) {
    let popupShown = false;

    // Add celebration animation (only if progress bar element exists)
    if (this.progressBar) {
      this.progressBar.style.animation = 'bounce 0.6s ease-in-out';
    }
    
    // Show popup for 3rd milestone if enabled
    if (milestone === 3 && theme.settings.cartProgressBar?.milestone3Popup?.enabled) {
      const manuallyRemoved = sessionStorage.getItem('milestone3-popup-manually-removed');
      const dismissedWithoutSelection = sessionStorage.getItem('milestone3-popup-dismissed-without-selection');
      if (manuallyRemoved !== 'true' && dismissedWithoutSelection !== 'true') {
        popupShown = await this.showMilestonePopup();
      }
    }
    
    // Add confetti effect if available
    if (window.confetti) {
      const confettiConfig = {
        particleCount: milestone === 3 ? 150 : 100, // More confetti for final milestone
        spread: 70,
        origin: { y: 0.6 }
      };
      
      window.confetti(confettiConfig);
      
      // Extra celebration for milestone 2 (free delivery)
      if (milestone === 2) {
        setTimeout(() => {
          window.confetti({
            particleCount: 50,
            spread: 50,
            origin: { y: 0.6 }
          });
        }, 300);
      }
    }

    // Show milestone achievement notification only when:
    // 1. Cart was modified (not on page load)
    // 2. Milestone is progressing forward (not going backwards)
    if (cartWasModified && isMovingForward) {
      this.showMilestoneNotification(milestone);
    }

    // Remove animation class after animation completes (only if progress bar exists)
    if (this.progressBar) {
      setTimeout(() => {
        this.progressBar.style.animation = '';
      }, 600);
    }

    return popupShown;
  }
}

// Singleton instance
let cartProgressBarInstance = null;

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (!cartProgressBarInstance) {
    cartProgressBarInstance = new CartProgressBar();
  }
});

// on:cart:updated and on:cart:add are already handled inside CartProgressBar.bindEvents()
// via the debounced _scheduleUpdateProgress(). Duplicate module-level listeners removed.

// Export for potential external use
window.CartProgressBar = CartProgressBar;